import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal } from "react-bootstrap";
import { ActionButton, AltButton } from "../../../common/Buttons/Buttons.jsx";
import { notify } from "../../../../assets/helpers/toast";
import { servScalePriceService } from "../../../../services/servScalePriceService";
import * as schemas from "../../../../assets/schemas/schemas";
import {
  RhfDateYmdField,
  RhfMoneyField,
  RhfPercentField,
  toYMD,
} from "../../../common/RhfParameterInputs/RhfParameterInputs.jsx";
import {
  servScaleDefaultValues,
  servScaleFormKeys,
  formatApplicationDateForModal,
} from "../../../common/RhfParameterInputs/rhfParameterInputsShared.js";
import { ServScalePriceHistorySection } from "./ServScalePriceHistorySection.jsx";


const BaremoConfigForm = ({
  onSave,
  handleCancel,
}) => {
  const { handleSubmit } = useFormContext();
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [pendingSavePayload, setPendingSavePayload] = useState(null);

  const requestSave = useCallback(() => {
    handleSubmit((data) => {
      setPendingSavePayload(data);
      setConfirmSaveOpen(true);
    })();
  }, [handleSubmit]);

  const closeConfirmSave = useCallback(() => {
    setConfirmSaveOpen(false);
    setPendingSavePayload(null);
  }, []);

  const confirmSave = useCallback(async () => {
    if (!pendingSavePayload) return;
    const ok = await onSave(pendingSavePayload);
    if (ok) closeConfirmSave();
  }, [onSave, pendingSavePayload, closeConfirmSave]);

  const applicationDateLabel = formatApplicationDateForModal(
    pendingSavePayload?.dateSince
  );

  return (
    <>
    <form
      className="baremo-section__form"
      onSubmit={(e) => {
        e.preventDefault();
        requestSave();
      }}
      noValidate
    >
      <div className="baremo-section__date-box">
        <div className="baremo-section__date-row">
          <RhfDateYmdField
            name="dateSince"
            label="Fecha de aplicación"
            id="baremo-dateSince"
            idPrefix="baremo"
          />
          <p className="baremo-section__date-hint">
            <span className="baremo-section__date-hint--muted">
              Los cambios aplicarán a partir de la fecha seleccionada.{" "}
            </span>
            <span className="baremo-section__date-hint--accent">
              Los servicios anteriores no serán afectados.
            </span>
          </p>
        </div>
      </div>

      <div className="baremo-section__subsection">
        <h3 className="baremo-section__subsection-title">
          Precios base del servicio
        </h3>
        <div className="baremo-section__grid baremo-section__grid--3">
          <RhfMoneyField
            idPrefix="baremo"
            name="priceHitch"
            label="Precio de enganche"
            suffix="$"
          />
          <RhfMoneyField
            idPrefix="baremo"
            name="priceKm"
            label="Monto por kilómetro"
            suffix="$/km"
          />
          <RhfPercentField
            idPrefix="baremo"
            name="nocturnalSurchargePct"
            label="% Recargo nocturno"
          />
        </div>
      </div>

      <div className="baremo-section__subsection">
        <h3 className="baremo-section__subsection-title">Montos mínimos</h3>
        <div className="baremo-section__grid baremo-section__grid--2">
          <RhfMoneyField
            idPrefix="baremo"
            name="minPriceDayInt"
            label="Monto mín. diurno — interno"
            suffix="$"
          />
          <RhfMoneyField
            idPrefix="baremo"
            name="minPriceDayExt"
            label="Monto mín. diurno — externo"
            suffix="$"
          />
          <RhfMoneyField
            idPrefix="baremo"
            name="minPriceNightInt"
            label="Monto mín. nocturno — interno"
            suffix="$"
          />
          <RhfMoneyField
            idPrefix="baremo"
            name="minPriceNightExt"
            label="Monto mín. nocturno — externo"
            suffix="$"
          />
        </div>
      </div>

      <div className="baremo-section__subsection">
        <h3 className="baremo-section__subsection-title">
          Distribución por tipo de gruero
        </h3>
        <div className="baremo-section__grid baremo-section__grid--2">
          <RhfPercentField
            idPrefix="baremo"
            name="intTowPricePct"
            label="% del monto — gruero interno"
          />
          <RhfPercentField
            idPrefix="baremo"
            name="extTowPricePct"
            label="% del monto — gruero externo"
          />
        </div>
      </div>

      <div className="baremo-section__subsection">
        <h3 className="baremo-section__subsection-title">
          % ganancia de choferes
        </h3>
        <div className="baremo-section__grid baremo-section__grid--3">
          <RhfPercentField
            idPrefix="baremo"
            name="extTowEarnPct"
            label="Gruero externo"
          />
          <RhfPercentField
            idPrefix="baremo"
            name="intTowEarnPct"
            label="Gruero interno"
          />
          <RhfPercentField
            idPrefix="baremo"
            name="avEarnPct"
            label="Asistente vial"
          />
        </div>
      </div>

      <div className="parameters-page__alarm-footer">
        <AltButton label="Cancelar" action={handleCancel} />
        <ActionButton label="Guardar cambios" action={requestSave} />
      </div>
    </form>

    <Modal
      centered
      show={confirmSaveOpen}
      onHide={closeConfirmSave}
      className="baremo-section__confirm-modal"
    >
      <Modal.Header closeButton className="modal-header">
        <Modal.Title as="h5">Confirmar guardado</Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-body">
        <p className="baremo-section__confirm-modal-text mb-0">
          Los cambios surtirán efecto a partir del{" "}
          <strong>{applicationDateLabel}</strong>, de acuerdo con la{" "}
          <strong>fecha de aplicación</strong> seleccionada en el formulario.
        </p>
      </Modal.Body>
      <Modal.Footer className="modal-footer baremo-section__confirm-modal-footer">
        <AltButton label="Cancelar" action={closeConfirmSave} />
        <ActionButton label="Guardar" action={confirmSave} />
      </Modal.Footer>
    </Modal>
    </>
  );
};

export const ServScalePriceParametersSection = () => {
  const [tab, setTab] = useState("config");
  const [loading, setLoading] = useState(true);
  const [formReady, setFormReady] = useState(false);
  const [lastApiData, setLastApiData] = useState(null);
  const savedFormSnapshotRef = useRef(null);

  const servScaleSchema = useMemo(
    () => schemas.servScalePriceFormSchema(),
    []
  );

  const formMethods = useForm({
    defaultValues: servScaleDefaultValues(),
    resolver: yupResolver(servScaleSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const { reset, getValues } = formMethods;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const response = await servScalePriceService.getLast();
      if (cancelled) return;
      setLoading(false);

      setLastApiData(response?.data != null ? response.data : null);

      if (response?.status === 200 && response.data) {
        const base = servScaleDefaultValues();
        const src = response.data?.data ?? response.data;
        const next =
          src && typeof src === "object"
            ? Object.fromEntries(
              servScaleFormKeys.map((k) => [
                k,
                k in src
                  ? k === "dateSince"
                    ? toYMD(src[k]) || base.dateSince
                    : src[k]
                  : base[k],
              ])
            )
            : base;
        reset(next);
        savedFormSnapshotRef.current = JSON.parse(JSON.stringify(next));
        setFormReady(true);
        return;
      }
      if (response?.status && response.status !== 200) {
        notify(
          response?.message || "No se pudo cargar la configuración de baremo",
          "error"
        );
      }
      const fallback = servScaleDefaultValues();
      reset(fallback);
      savedFormSnapshotRef.current = JSON.parse(JSON.stringify(fallback));
      setFormReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [reset]);

  const handleCancel = useCallback(() => {
    if (savedFormSnapshotRef.current) {
      reset(JSON.parse(JSON.stringify(savedFormSnapshotRef.current)));
    }
  }, [reset]);

  const onSave = useCallback(
    async (formData) => {
      const response = await servScalePriceService.save(formData);
      if (response?.status === 200) {
        const snapshot = { ...getValues() };
        savedFormSnapshotRef.current = JSON.parse(JSON.stringify(snapshot));
        notify("Cambios guardados", "success");
        return true;
      }
      notify(
        response?.message || "No se pudieron guardar los parámetros",
        "error"
      );
      return false;
    },
    [getValues]
  );

  if (loading || !formReady) {
    return (
      <div className="baremo-section baremo-section--loading">
        Cargando parámetros…
      </div>
    );
  }

  return (
    <div
      className="baremo-section"
      data-api-payload={lastApiData != null ? "1" : "0"}
    >
      <FormProvider {...formMethods}>
        <div className="baremo-section__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "config"}
            className={`baremo-section__tab ${tab === "config" ? "baremo-section__tab--active" : ""
              }`}
            onClick={() => setTab("config")}
          >
            Configuración actual
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "history"}
            className={`baremo-section__tab ${tab === "history" ? "baremo-section__tab--active" : ""
              }`}
            onClick={() => setTab("history")}
          >
            Historial de cambios
          </button>
        </div>

        {tab === "history" ? (
          <ServScalePriceHistorySection />
        ) : (
          <BaremoConfigForm onSave={onSave} handleCancel={handleCancel} />
        )}
      </FormProvider>
    </div>
  );
};
